import { ActivatedRoute, Data, Router, RouterLink } from '@angular/router';
import { CardImageComponent } from '@components/card-image/card-image.component';
import { Component, inject, signal } from '@angular/core';
import { HttpParams, httpResource } from '@angular/common/http';
import { HumanReadableSizePipe, MarkdownPipe } from '@pipes';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { PromptComponent } from '@components/prompt/prompt.component';

// Models

import {
  AttachmentListResponse,
  ContributorRole,
  MapArchiveEntry,
  MapLicense,
  MapOrg,
  MapOrgRole,
  Permission,
  ScanDetails,
} from '@models';

// Services

import {
  AttachmentService,
  AuthService,
  MapArchiveService,
  NotificationService,
} from '@services';

import { environment } from '@environment';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-map-archive-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  imports: [
    CardImageComponent,
    HumanReadableSizePipe,
    LeafletGroupComponent,
    MarkdownPipe,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDividerModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTooltipModule,
    RouterLink,
  ],
})
export class MapArchiveItemComponent {
  // Router
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  attachmentService = inject(AttachmentService);
  authService = inject(AuthService);
  mapArchiveService = inject(MapArchiveService);
  notificationService = inject(NotificationService);

  // Dialog
  dialog = inject(MatDialog);

  // Environment Variables
  kinko = environment.kinkoHost;
  mapDataset = environment.mapArchiveDatasetId;

  // Fetch the scan list
  defaultPageSize = 12;
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || this.defaultPageSize);

  imageList = httpResource<AttachmentListResponse>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize())
      .set('count', true)
      .set('prefix', '/source/')
      .set('recordId', this.mapEntry().id)
      .set('order', 'created:asc')

    return `${environment.kinkoHost}/dataset/${environment.mapArchiveDatasetId}/attachment/?${httpParams.toString()}`;
  });

  imagePageChange(event: PageEvent) {
    this.page.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  // Base URLs to fetch web preview and thumbnail
  thumbBaseAddr =
    this.kinko +
    '/dataset/' +
    this.mapDataset +
    '/attachment/_blob' +
    '?prefix=/processed/thumbnail/' +
    '&skip=0&take=1&count=true&order=created:asc';
  webBaseAddr =
    this.kinko +
    '/dataset/' +
    this.mapDataset +
    '/attachment/_blob' +
    '?prefix=/processed/web/' +
    '&skip=0&take=1&count=true&order=created:asc';

  mapEntry = signal<MapArchiveEntry>(this.route.snapshot.data['map']);
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  // Enum declarations
  contribRole = ContributorRole;
  orgRole = MapOrgRole;
  mapLicense = MapLicense;

  constructor() {
    // Return 404 page if no map archive dataset is configured
    if (this.mapDataset === '') {
      this.router.navigate(['/404'], {
        skipLocationChange: true,
      });
      this.notificationService.error('No map archive dataset configured.');
    }

    // Subscribe to route data events
    this.route.data.subscribe((data: Data) => {
      this.mapEntry.set(data['map']);
      this.permissions.set(data['permissions']);
    });
  }

  deleteMap() {
    const dialogRef = this.dialog.open(PromptComponent, {
      data: {
        title: 'Delete map',
        content: `Confirm that you want to delete this map entry.


          If the map has one or more images uploaded to it
          you have to remove those manually before you can
          delete this entry.`,
        minWidth: '360px',
        width: '480px',
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.mapArchiveService.removeMap(this.mapEntry().id).subscribe({
        next: () => {
          this.router.navigate(['/map/archive']);
          this.notificationService.success('Map deleted successfully.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to delete map.');
        },
      });
    });
  }

  // Look at the contributor and organisation list to find the publisher.
  // Currently we assume and return only one publisher.
  getPublisher(): string {
    if (this.mapEntry().content.contributors) {
      for (const contrib of this.mapEntry().content.contributors) {
        if (
          this.mapEntry().content.scans &&
          contrib.roles.includes(this.contribRole['Publisher'])
        ) {
          return contrib.name;
        }
      }
    }

    if (this.mapEntry().content.organisations) {
      for (const org of this.mapEntry().content.organisations) {
        if (
          this.mapEntry().content.scans &&
          org.roles.includes(this.orgRole['Publisher'])
        ) {
          return org.name;
        }
      }
    }

    return 'Unknown';
  }

  // Convert scale array to a comma separated string
  getScaleList(): string {
    if (this.mapEntry().content && this.mapEntry().content.scales) {
      const scales = this.mapEntry().content.scales || [];
      if (scales.length) {
        return scales
          .map((s: string) => {
            return '1:' + s;
          })
          .join(', ');
      }
    }

    return '';
  }

  // Returns true if the original license of one of the map images is shown
  // as public domain because of the publication date
  shownAsPublicDomain(): boolean {
    const publication = this.mapEntry().content.scans;
    const d = new Date();

    const images = this.imageList.value()?.items;
    if (!images?.length) {
      return false;
    }

    for (const img of images) {
      if (publication && publication[img.id]) {
        const license = publication[img.id].license;
        const year = publication[img.id].publicationYear;

        if (year && license !== this.mapLicense['public domain']) {
          if (d.getFullYear() - year > 70) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // Returns true if the original license is 'public domain' or if a period of
  // 70 years has passed since publication. This means that the original rights
  // have expired and the image is now in the public domain.
  isPublicDomain(scan: ScanDetails): boolean {
    if (scan.license === this.mapLicense['public domain']) {
      return true;
    }

    if (!scan.publicationYear) {
      return false;
    }

    const currentYear = new Date().getFullYear();
    return currentYear - scan.publicationYear > 70;
  }

  // Return the license string for the given scan. All licenses that aren't
  // CC0 get rendered as public domain mark 1.0 ( rights expired) when the
  // rights period has expired (70 years).
  getLicenseLabel(scan: ScanDetails): string {
    if (scan.license != this.mapLicense['CC0']) {
      if (this.isPublicDomain(scan)) {
        return this.mapLicense['public domain'];
      }
    }

    return scan.license;
  }

  getArchiveInfo(org: MapOrg): string {
    let info = '';

    if (org.archiveInfo?.room) {
      info += 'Room: ' + org.archiveInfo?.room + ', ';
    }
    if (org.archiveInfo?.copies) {
      info += 'Copies: ' + org.archiveInfo?.copies;
    }
    if (org.archiveInfo?.storageRef) {
      info += ', Ref: ' + org.archiveInfo?.storageRef;
    }

    return info;
  }
}
